import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useReadAloud } from '../../contexts/ReadAloudContext';
import './ReadAloudControlBar.css';

/**
 * ReadAloudControlBar - A persistent control bar for read-aloud functionality
 * Appears at the bottom of the screen when a story is being read aloud
 */
const ReadAloudControlBar = () => {
  const navigate = useNavigate();
  const { 
    readAloudStory, 
    isPlaying, 
    currentPageIndex, 
    togglePlayPause, 
    unloadReading 
  } = useReadAloud();
  
  // Only render if a story is loaded
  if (!readAloudStory) return null;
  
  // Always use standardized direct object format in the new architecture
  const storyData = readAloudStory;
  
  // Add a spacer to prevent content from being hidden behind the bar
  const Spacer = () => <div className="read-aloud-control-bar-spacer" aria-hidden="true" />;
  
  // Get total pages for display - use the story's total_pages field if available
  const totalPages = storyData.total_pages || storyData.pages?.length || 1;
  
  // Navigate to the story view at the current page being read aloud
  const navigateToCurrentPage = () => {
    // We need to explicitly set state in StoryView by passing the page index as a URL parameter
    navigate(`/weaversworld/story/${storyData.id}?pageIndex=${currentPageIndex}`);
  };
  
  return (
    <>
      <div 
        className="read-aloud-control-bar" 
        data-testid="read-aloud-control-bar"
        role="region" 
        aria-label="Story read-aloud controls"
      >
        <div 
          className="story-info"
          onClick={navigateToCurrentPage}
          role="button"
          aria-label={`Navigate to ${storyData.title} page ${currentPageIndex + 1}`}
        >
          <span className="story-title">{storyData.title}</span>
          <span className="page-indicator">
            Page {currentPageIndex + 1}/{totalPages}
          </span>
        </div>
        <div className="controls">
          <button 
            className={`play-pause-button ${isPlaying ? 'playing' : 'paused'}`}
            onClick={togglePlayPause}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            title={isPlaying ? 'Pause reading' : 'Play reading'}
            data-testid="play-pause-button"
          >
            <span style={{ display: isPlaying ? 'inline' : 'none' }}>⏸</span>
            <span style={{ display: isPlaying ? 'none' : 'inline' }}>▶️</span>
          </button>
          <button 
            className="stop-button"
            onClick={unloadReading}
            aria-label="Stop and close read-aloud"
            title="Stop and close read-aloud"
            data-testid="stop-button"
          >
            ⏹
          </button>
        </div>
      </div>
      <Spacer />
    </>
  );
};

export default ReadAloudControlBar;